import mixpanel from 'mixpanel-browser';
import ClientConfig from 'src/config/config.ts';
import { v4 as generateUUID } from 'uuid';
import {
  ANALYTICS_ENGINES,
  ANALYTICS_EVENT_TYPES,
  ANALYTICS_PRODUCT_NAME,
  ANALYTICS_TRACKING_TYPES,
} from '../analytics-enums';
import { isUserEmailExcludedFromAnalyticsTracking } from '../analytics-exclude-list';

let initialized = false;

const MIXPANEL_SESSION_ID = 'mixpanelSessionID';

const ensureInit = (email?: string): boolean => {
  try {
    if (initialized) {
      return true;
    }
    const apiHost = ClientConfig.config.mixpanelApiHost;
    const token = ClientConfig.config.mixpanelApiKey;
    const enabled = ClientConfig.config.enableMixpanel;
    const debug = ClientConfig.config.enableAnalyticsDebugLogs;
    if (!enabled || !token || !email || isUserEmailExcludedFromAnalyticsTracking(email) || !getMixpanelSessionID()) {
      return false;
    }
    /*
      Analytics consent and GDPR are handled via our "general terms" customer contracts
      See https://oliasoft.atlassian.net/browse/OW-23899
      Therefore we always opt users in: https://oliasoft.atlassian.net/browse/OW-23647
    */
    mixpanel.init(token, {
      api_host: apiHost,
      debug,
      track_pageview: false,
      ignore_dnt: true, // Ignore the Do Not Track in browsers that support it (OW-23647), https://en.wikipedia.org/wiki/Do_Not_Track
    });
    if (mixpanel.has_opted_out_tracking()) {
      mixpanel.opt_in_tracking(); // Always opt user in to analytics data collection (OW-23647)
    }
    initialized = true;
    return true;
  } catch (e) {
    console.error('Mixpanel init failed', e);
    return false;
  }
};

export const applyMixpanelIdentity = (email: string) => {
  try {
    if (!ensureInit(email)) {
      return;
    }
    mixpanel.identify(email);
  } catch (e) {
    console.error('Mixpanel identify failed', e);
  }
};

export const trackEvent = (name: string, props: Record<string, any> = {}) => {
  try {
    if (!ensureInit()) {
      return;
    }
    const mixpanelSessionID = getMixpanelSessionID();
    const { enableAnalyticsDebugLogs } = ClientConfig.config;
    const extendedProps = {
      product_name: ANALYTICS_PRODUCT_NAME,
      session_id: mixpanelSessionID,
      timestamp: new Date().toISOString(),
      ...props,
    };
    if (enableAnalyticsDebugLogs) {
      console.log({
        engine: ANALYTICS_ENGINES.MIXPANEL,
        event_type: name,
        ...extendedProps,
      });
    }
    mixpanel.track(name, extendedProps);
  } catch (e) {
    console.error('Mixpanel trackEvent failed', e);
  }
};

export const trackClickEvent = (analyticsFeatureName: string) => {
  if (analyticsFeatureName) {
    trackEvent(ANALYTICS_TRACKING_TYPES.FEATURE_CLICKED, {
      feature: analyticsFeatureName,
      action: ANALYTICS_EVENT_TYPES.CLICK,
    });
  }
};

export const trackNavigationEvent = ({
  moduleName,
  moduleUrl,
  startTime,
  endTime,
}: {
  moduleName: string;
  moduleUrl: string;
  startTime: number;
  endTime: number;
}) => {
  try {
    if (!ensureInit()) {
      return;
    }
    const mixpanelSessionID = getMixpanelSessionID();
    const durationInSeconds = (endTime - startTime) / 1000;
    trackEvent(ANALYTICS_TRACKING_TYPES.PAGE_VIEW, {
      current_page_title: moduleName,
      current_url: moduleUrl,
      session_id: mixpanelSessionID,
      duration_in_seconds_custom: durationInSeconds, // Mixpanel has built-in Duration property (including ours too for comparison)
    });
  } catch (e) {
    console.error('Mixpanel trackNavigationEvent failed', e);
  }
};

export const resetMixpanel = () => {
  try {
    if (!ensureInit()) {
      return;
    }

    mixpanel.reset();
  } catch (e) {
    console.error('Mixpanel reset failed', e);
  }
};

export const startTimingTrackingEvent = (analyticsTrackingType: string) => {
  try {
    if (!ensureInit()) {
      return;
    }
    if (analyticsTrackingType) {
      mixpanel.time_event(analyticsTrackingType);
    }
  } catch (e) {
    console.error('Mixpanel startTimingTrackingEvent failed', e);
  }
};

const getMixpanelSessionID = () => {
  return sessionStorage.getItem(MIXPANEL_SESSION_ID);
};

export const generateMixpanelSessionID = () => {
  const session = sessionStorage.getItem(MIXPANEL_SESSION_ID);
  if (!session) {
    sessionStorage.setItem(MIXPANEL_SESSION_ID, generateUUID());
  }
};
